import { useState, useEffect, useCallback } from 'react';
import { LocationData, WeatherError, WeatherErrorType } from '../../api/weather/types';
import { LocationService } from '../../api/weather/locationService';
import { FallbackLocationData, WeatherErrorHandler } from '../../api/weather/utils/errorHandler';

interface UseLocationState {
  location: LocationData | FallbackLocationData | null;
  isLoading: boolean;
  error: string | null;
  isFallback: boolean;
  fallbackReason?: string;
  errorSeverity?: 'low' | 'medium' | 'high';
}

interface UseLocationReturn extends UseLocationState {
  refetch: () => Promise<void>;
  clearCache: () => void;
  hasRecoverableError: boolean;
}

export const useLocation = (): UseLocationReturn => {
  const [state, setState] = useState<UseLocationState>({
    location: null,
    isLoading: true,
    error: null,
    isFallback: false,
  });

  const fetchLocation = useCallback(async () => {
    setState(prev => ({ 
      ...prev, 
      isLoading: true, 
      error: null,
      isFallback: false 
    }));

    try {
      const location = await LocationService.getCurrentLocation();
      
      // Check if this is fallback data
      const isFallback = 'isFallback' in location && location.isFallback;
      const fallbackReason = isFallback ? (location as FallbackLocationData).fallbackReason : undefined;
      
      setState({
        location,
        isLoading: false,
        error: null,
        isFallback,
        fallbackReason,
        errorSeverity: undefined
      });
    } catch (error) {
      const locationError = error instanceof WeatherError ? error : new WeatherError(
        'location_detection_failed' as WeatherErrorType,
        'Failed to detect location',
        error as Error
      );
      
      const errorSeverity = WeatherErrorHandler.getErrorSeverity(locationError);
      const userMessage = WeatherErrorHandler.createUserFriendlyMessage(locationError);
      
      setState({
        location: null,
        isLoading: false,
        error: userMessage,
        isFallback: false,
        errorSeverity
      });
    }
  }, []);

  const refetch = useCallback(async () => {
    await fetchLocation();
  }, [fetchLocation]);

  const clearCache = useCallback(() => {
    LocationService.clearLocationCache();
  }, []);

  useEffect(() => {
    fetchLocation();
  }, [fetchLocation]);

  const hasRecoverableError = state.error ? 
    WeatherErrorHandler.isRecoverableError(new Error(state.error)) : 
    false;

  return {
    ...state,
    refetch,
    clearCache,
    hasRecoverableError,
  };
};